import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';

import config from '../../config';
import { types as sdkTypes } from '../../util/sdkLoader';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { signup } from '../../ducks/Auth.duck';
import {
  Modal,
  CustomerSignupLogos,
  LayoutSingleColumn,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  Page,
  TermsOfService,
  TopbarBlank,
} from '../../components';
import {
  CustomerSignupStep1Form,
  CustomerSignupStep2Form,
  CustomerSignupStep3Form,
} from '../../forms';

import quoteImg1 from '../../assets/systam-success-story-quote2.webp';
import quoteImg2 from '../../assets/haltian-success-story-quote.png';
import quoteImg3 from '../../assets/corego-success-story-quote.png';

import css from './CustomerSignupPage.css';

const { LatLng } = sdkTypes;

const TESTIMONIAL_INTERVAL = 5000;
const TOTAL_STEPS = 3;

const reviveLocation = location => {
  if (!location || !location.selectedPlace || !location.selectedPlace.origin) {
    return location;
  }
  const { origin } = location.selectedPlace;
  if (origin instanceof LatLng) {
    return location;
  }
  return {
    ...location,
    selectedPlace: {
      ...location.selectedPlace,
      origin: new LatLng(origin.lat, origin.lng),
    },
  };
};

const testimonials = [
  {
    textId: 'SolutionPageCustomerReviews.reviewText1',
    nameId: 'SolutionPageCustomerReviews.reviewName1',
    titleId: 'SolutionPageCustomerReviews.reviewTitle1',
    img: quoteImg1,
  },
  {
    textId: 'SolutionPageCustomerReviews.reviewText2',
    nameId: 'SolutionPageCustomerReviews.reviewName2',
    titleId: 'SolutionPageCustomerReviews.reviewTitle2',
    img: quoteImg2,
  },
  {
    textId: 'SolutionPageCustomerReviews.reviewText4',
    nameId: 'SolutionPageCustomerReviews.reviewName4',
    titleId: 'SolutionPageCustomerReviews.reviewTitle4',
    img: quoteImg3,
  },
];

const initialFormData = {};

const getCurrentStep = () => {
  if (typeof window !== 'undefined' && !!window.sessionStorage) {
    return parseInt(sessionStorage.getItem('customerSignupStep'), 10) || 0;
  }
  return 0;
};

const getStoredFormData = () => {
  if (typeof window !== 'undefined' && !!window.sessionStorage) {
    const stored = JSON.parse(sessionStorage.getItem('customerFormData')) || initialFormData;
    return stored.location ? { ...stored, location: reviveLocation(stored.location) } : stored;
  }
  return initialFormData;
};

const TestimonialCarousel = ({ intl }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [direction, setDirection] = useState('next');
  const currentIndexRef = useRef(0);
  const animatingRef = useRef(false);

  const goTo = useCallback((getNext, dir) => {
    if (animatingRef.current) return;
    animatingRef.current = true;
    const cur = currentIndexRef.current;
    const next = getNext(cur);
    currentIndexRef.current = next;
    setDirection(dir);
    setPrevIndex(cur);
    setCurrentIndex(next);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo(cur => (cur + 1) % testimonials.length, 'next');
    }, TESTIMONIAL_INTERVAL);
    return () => clearInterval(timer);
  }, [goTo]);

  const goPrev = () => goTo(cur => (cur - 1 + testimonials.length) % testimonials.length, 'prev');
  const goNext = () => goTo(cur => (cur + 1) % testimonials.length, 'next');
  const goToDot = i => {
    const cur = currentIndexRef.current;
    if (i === cur) return;
    goTo(() => i, i > cur ? 'next' : 'prev');
  };

  const handleAnimationEnd = () => {
    animatingRef.current = false;
    setPrevIndex(null);
  };

  const current = testimonials[currentIndex];
  const leaving = prevIndex !== null ? testimonials[prevIndex] : null;

  const enterClass = direction === 'next' ? css.slideInRight : css.slideInLeft;
  const exitClass = direction === 'next' ? css.slideOutLeft : css.slideOutRight;

  const renderItem = (t, extraClass, onEnd) => (
    <div
      className={`${css.testimonialMain}${extraClass ? ` ${extraClass}` : ''}`}
      onAnimationEnd={onEnd || undefined}
    >
      <img
        className={css.testimonialAvatar}
        src={t.img}
        alt={intl.formatMessage({ id: t.nameId })}
      />
      <p className={css.testimonialQuote}>{intl.formatMessage({ id: t.textId })}</p>
      <div>
        <p className={css.testimonialAuthorName}>{intl.formatMessage({ id: t.nameId })}</p>
        <p className={css.testimonialAuthorTitle}>{intl.formatMessage({ id: t.titleId })}</p>
      </div>
    </div>
  );

  return (
    <div className={css.testimonialSection}>
      <div className={css.testimonialContent}>
        <button type="button" className={css.testimonialArrow} onClick={goPrev}>
          &#8249;
        </button>
        <div className={css.testimonialSlider}>
          {leaving && renderItem(leaving, exitClass, null)}
          {renderItem(current, leaving ? enterClass : '', handleAnimationEnd)}
        </div>
        <button type="button" className={css.testimonialArrow} onClick={goNext}>
          &#8250;
        </button>
      </div>
      <div className={css.testimonialDots}>
        {testimonials.map((_, i) => (
          <button
            key={i}
            type="button"
            className={i === currentIndex ? css.testimonialDotActive : css.testimonialDot}
            onClick={() => goToDot(i)}
          />
        ))}
      </div>
    </div>
  );
};

const CustomerSignupPageComponent = props => {
  const {
    scrollingDisabled,
    onManageDisableScrolling,
    onSignup,
    signupInProgress,
    signupError,
    intl,
  } = props;

  const [step, setStep] = useState(getCurrentStep());
  const [formData, setFormData] = useState(getStoredFormData());
  const [tosModalOpen, setTosModalOpen] = useState(false);

  useEffect(() => {
    setFormData(getStoredFormData());
  }, [step]);

  const saveStepToStorage = newStep => {
    if (typeof window !== 'undefined' && !!window.sessionStorage) {
      sessionStorage.setItem('customerSignupStep', newStep);
    }
  };

  const saveFormDataToStorage = data => {
    if (typeof window !== 'undefined' && !!window.sessionStorage) {
      sessionStorage.setItem('customerFormData', JSON.stringify(data));
    }
  };

  const next = useCallback(
    data => {
      const newFormData = { ...formData, ...data };
      setFormData(newFormData);
      saveFormDataToStorage(newFormData);

      const newStep = step + 1;
      setStep(newStep);
      saveStepToStorage(newStep);
    },
    [formData, step]
  );

  const prev = useCallback(
    data => {
      const newFormData = data ? { ...formData, ...data } : formData;
      if (data) {
        setFormData(newFormData);
        saveFormDataToStorage(newFormData);
      }

      const newStep = step - 1;
      setStep(newStep);
      saveStepToStorage(newStep);
    },
    [formData, step]
  );

  const handleFinalSubmit = useCallback(
    data => {
      const allData = { ...formData, ...data };
      setFormData(allData);
      saveFormDataToStorage(allData);

      const params = {
        email: allData.email,
        password: allData.password,
        firstName: allData.fname ? allData.fname.trim() : '',
        lastName: allData.lname ? allData.lname.trim() : '',
        account_role: 'customer',
        phoneNumber: allData.phoneNumber ? '+' + allData.phoneNumber : '',
        howDidYouHear: allData.howDidYouHear,
        howDidYouHearOther:
          allData.howDidYouHear === 'other' ? allData.howDidYouHearOther : undefined,
        lookingFor: allData.lookingFor,
        lookingForText: allData.lookingForText,
        roleAtCompany: allData.roleAtCompany,
        workedWithFreelancers: allData.workedWithFreelancers,
        languages: allData.languages,
        location: allData.location,
      };

      onSignup(params).then(() => {
        if (typeof window !== 'undefined' && !!window.sessionStorage) {
          sessionStorage.removeItem('customerFormData');
          sessionStorage.removeItem('customerSignupStep');
        }
      });
    },
    [formData, onSignup]
  );

  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'CustomerSignupPage.schemaTitle' }, { siteTitle });

  const stepLabel = `${intl.formatMessage({ id: 'CustomerSignupForms.stepLabel' })} ${step +
    1}/${TOTAL_STEPS}`;

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <CustomerSignupStep1Form
            onSubmit={next}
            initialValues={formData}
            onOpenTermsOfService={() => setTosModalOpen(true)}
          />
        );
      case 1:
        return <CustomerSignupStep2Form onSubmit={next} onPrev={prev} initialValues={formData} />;
      case 2:
        return (
          <CustomerSignupStep3Form
            onSubmit={handleFinalSubmit}
            onPrev={prev}
            initialValues={formData}
            submitInProgress={signupInProgress}
            onOpenTermsOfService={() => setTosModalOpen(true)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>{/* <TopbarBlank /> */}</LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.pageWrapper}>
            {/* Left panel - Form */}
            <div className={css.leftPanel}>
              <div className={css.formCard}>
                <div className={css.formHeader}>
                  <h1 className={css.formTitle}>
                    <FormattedMessage
                      id={
                        step === 0
                          ? 'CustomerSignupForms.formTitle'
                          : step === 1
                          ? 'CustomerSignupForms.step2Heading'
                          : 'CustomerSignupForms.lookingForLabel'
                      }
                    />
                  </h1>
                  <span className={css.stepIndicator}>{stepLabel}</span>
                </div>
                {step === 1 ? (
                  <p className={css.formSubtitle}>
                    <FormattedMessage id="CustomerSignupForms.step2HeadingSubtitle" />
                  </p>
                ) : null}
                {step === 2 ? (
                  <p className={css.formSubtitle}>
                    <FormattedMessage id="CustomerSignupForms.step2Subtitle" />
                  </p>
                ) : null}
                {renderStep()}
                {signupError ? (
                  <div className={css.signupError}>
                    <FormattedMessage id="AuthenticationPage.signupFailed" />
                  </div>
                ) : null}
              </div>
            </div>

            {/* Right panel - Testimonial + Logos */}
            <div className={css.rightPanel}>
              <TestimonialCarousel intl={intl} />
              <div className={css.customerLogos}>
                <CustomerSignupLogos />
              </div>
            </div>
          </div>

          <Modal
            id="CustomerSignupPage.tos"
            isOpen={tosModalOpen}
            onClose={() => setTosModalOpen(false)}
            usePortal
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.modal}>
              <h2>
                <FormattedMessage id="AuthenticationPage.termsHeading" />
              </h2>
              <TermsOfService />
            </div>
          </Modal>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { signupInProgress, signupError } = state.Auth;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    signupInProgress,
    signupError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onSignup: params => dispatch(signup(params)),
});

const CustomerSignupPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(CustomerSignupPageComponent);

export default CustomerSignupPage;
